import React, { useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import moment from "moment";
import { ResponsiveLine } from "@nivo/line";
import { useTheme } from "@mui/material/styles";
import MUITypography from "@mui/material/Typography";
import MUIBox from "@mui/material/Box";
import MUIStack from "@mui/material/Stack";

import Format from "../../../Utility/Format";
import Widget from "../../Widget";

/**
 * Builds calendar-day totals for the configured number of recent historical days.
 *
 * @param {array} userProductionRecords User production records returned by the widget endpoint.
 * @param {number} displayDayCount Number of historical days to display.
 * @returns {Array<{dateKey: string, dateLabel: string, count: number}>}
 */
function buildUserProductionChartEntries(userProductionRecords, displayDayCount) {
    const productionCountByDateKey = userProductionRecords.reduce((countByDateKey, userProductionRecord) => {
        if (userProductionRecord.deleted === true) {
            return countByDateKey;
        }

        const dateKey = moment(userProductionRecord.date).format("YYYY-MM-DD");
        countByDateKey[dateKey] = (countByDateKey[dateKey] || 0) + Number(userProductionRecord.count);
        return countByDateKey;
    }, {});

    const chartEntries = [];
    const startOfToday = moment().startOf("day");
    const productionDate = moment(startOfToday).subtract(displayDayCount, "days");
    while (productionDate.isBefore(startOfToday, "day")) {
        const dateKey = productionDate.format("YYYY-MM-DD");
        chartEntries.push({
            dateKey: dateKey,
            dateLabel: productionDate.format("ddd M/D"),
            count: productionCountByDateKey[dateKey] || 0,
        });
        productionDate.add(1, "day");
    }

    return chartEntries;
}

/**
 * Sums all production categories recorded on the current date.
 *
 * @param {array} userProductionRecords User production records returned by the widget endpoint.
 * @returns {number}
 */
function sumTodayUserProductionCount(userProductionRecords) {
    const todayDateKey = moment().format("YYYY-MM-DD");
    return userProductionRecords.reduce((todayProductionCount, userProductionRecord) => {
        if (
            userProductionRecord.deleted === true ||
            moment(userProductionRecord.date).format("YYYY-MM-DD") !== todayDateKey
        ) {
            return todayProductionCount;
        }

        return todayProductionCount + Number(userProductionRecord.count);
    }, 0);
}

/**
 * Calculates average daily production across the full requested result period.
 *
 * @param {array} userProductionRecords User production records returned by the widget endpoint.
 * @param {number} requestedDayCount Number of days requested by the relative begin-date configuration.
 * @returns {number}
 */
function calculateUserProductionDailyAverage(userProductionRecords, requestedDayCount) {
    const totalProductionCount = userProductionRecords.reduce((productionCount, userProductionRecord) => {
        if (userProductionRecord.deleted === true) {
            return productionCount;
        }
        return productionCount + Number(userProductionRecord.count);
    }, 0);

    return totalProductionCount / requestedDayCount;
}

/**
 * Renders the configured historical production trend as a filled line sparkline.
 *
 * @param {object} props
 * @param {Array<{dateKey: string, dateLabel: string, count: number}>} props.chartEntries Historical calendar-day production totals.
 * @param {number|null|undefined} props.metric Configured daily production metric.
 * @returns {React.ReactElement}
 */
function WidgetDashboardUserProductionChart(props) {
    const materialUiTheme = useTheme();
    const chartContainerElementReference = useRef(null);
    const [productionChartTooltipState, setProductionChartTooltipState] = useState({
        isVisible: false,
        anchorLeftPixels: 0,
        anchorTopPixels: 0,
        tooltipTitleText: "",
        tooltipCount: 0,
        tooltipDataIndex: null,
    });

    const chartData = useMemo(() => {
        return [{
            id: "Production",
            data: props.chartEntries.map((chartEntry) => ({
                x: chartEntry.dateLabel,
                y: chartEntry.count,
            })),
        }];
    }, [props.chartEntries]);
    const chartMaximumValue = Math.max(
        1,
        ...props.chartEntries.map((chartEntry) => chartEntry.count),
        props.metric === null || props.metric === undefined ? 0 : Number(props.metric)
    ) * 1.1;

    const chartMarkers = useMemo(() => {
        if (props.metric === null || props.metric === undefined) {
            return [];
        }

        return [{
            axis: "y",
            value: Number(props.metric),
            lineStyle: {
                stroke: materialUiTheme.palette.text.secondary,
                strokeWidth: 1,
                strokeDasharray: "3 3",
            },
        }];
    }, [materialUiTheme, props.metric]);

    /**
     * Shows the portal tooltip for the historical date under the pointer.
     *
     * @param {number} hoveredDateIndex Index of the historical date being hovered.
     * @param {React.PointerEvent} pointerEvent Pointer event from the date overlay.
     * @returns {void}
     */
    const handleDatePointerEnter = (hoveredDateIndex, pointerEvent) => {
        const chartContainerElement = chartContainerElementReference.current;
        if (chartContainerElement === null || chartContainerElement === undefined) {
            return;
        }

        const tooltipChartEntry = props.chartEntries[hoveredDateIndex];
        if (tooltipChartEntry === undefined) {
            return;
        }

        const hoveredDateBoundingRectangle = pointerEvent.currentTarget.getBoundingClientRect();
        const chartContainerBoundingRectangle = chartContainerElement.getBoundingClientRect();
        const nextAnchorLeftPixels =
            hoveredDateBoundingRectangle.left + hoveredDateBoundingRectangle.width / 2;
        const nextAnchorTopPixels = chartContainerBoundingRectangle.bottom;

        setProductionChartTooltipState((previousTooltipState) => {
            if (
                previousTooltipState.isVisible === true &&
                previousTooltipState.tooltipDataIndex === hoveredDateIndex &&
                previousTooltipState.anchorLeftPixels === nextAnchorLeftPixels &&
                previousTooltipState.anchorTopPixels === nextAnchorTopPixels
            ) {
                return previousTooltipState;
            }

            return {
                isVisible: true,
                anchorLeftPixels: nextAnchorLeftPixels,
                anchorTopPixels: nextAnchorTopPixels,
                tooltipTitleText: tooltipChartEntry.dateLabel,
                tooltipCount: tooltipChartEntry.count,
                tooltipDataIndex: hoveredDateIndex,
            };
        });
    };

    /**
     * Hides the historical production tooltip when the pointer leaves the chart.
     *
     * @returns {void}
     */
    const handleChartPointerLeave = () => {
        setProductionChartTooltipState((previousTooltipState) => {
            if (previousTooltipState.isVisible === false) {
                return previousTooltipState;
            }
            return { ...previousTooltipState, isVisible: false };
        });
    };

    return (
        <>
            <MUIBox
                ref={chartContainerElementReference}
                onPointerLeave={handleChartPointerLeave}
                sx={{
                    position: "absolute",
                    inset: 0,
                    minHeight: 58,
                }}
            >
                <ResponsiveLine
                    data={chartData}
                    margin={{ top: 5, right: 4, bottom: 5, left: 4 }}
                    xScale={{ type: "point" }}
                    yScale={{ type: "linear", min: 0, max: chartMaximumValue }}
                    curve="monotoneX"
                    colors={[materialUiTheme.palette.primary.main]}
                    lineWidth={2}
                    enableArea={true}
                    areaOpacity={0.14}
                    pointSize={6}
                    pointColor={materialUiTheme.palette.primary.main}
                    pointBorderWidth={0}
                    enableGridX={false}
                    enableGridY={false}
                    axisTop={null}
                    axisRight={null}
                    axisBottom={null}
                    axisLeft={null}
                    markers={chartMarkers}
                    isInteractive={false}
                    animate={false}
                />
                <MUIStack
                    direction="row"
                    sx={{
                        position: "absolute",
                        inset: 0,
                    }}
                >
                    {props.chartEntries.map((chartEntry, chartEntryIndex) => (
                        <MUIBox
                            key={chartEntry.dateKey}
                            onPointerEnter={(pointerEvent) => handleDatePointerEnter(chartEntryIndex, pointerEvent)}
                            onPointerMove={(pointerEvent) => handleDatePointerEnter(chartEntryIndex, pointerEvent)}
                            sx={{
                                flex: 1,
                                height: "100%",
                            }}
                        />
                    ))}
                </MUIStack>
            </MUIBox>
            {productionChartTooltipState.isVisible === true && ReactDOM.createPortal(
                <MUIBox
                    sx={{
                        position: "fixed",
                        left: `${productionChartTooltipState.anchorLeftPixels}px`,
                        top: `${productionChartTooltipState.anchorTopPixels}px`,
                        transform: "translate(-50%, 8px)",
                        pointerEvents: "none",
                        backgroundColor: (theme) => theme.palette.background.paper,
                        color: (theme) => theme.palette.text.primary,
                        padding: "6px 8px",
                        borderRadius: 1,
                        fontSize: "0.9rem",
                        lineHeight: 1.3,
                        whiteSpace: "nowrap",
                        zIndex: (theme) => theme.zIndex.tooltip,
                        boxShadow: 4,
                    }}
                >
                    <MUITypography component="div" variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {productionChartTooltipState.tooltipTitleText}
                    </MUITypography>
                    <MUIStack direction="row" alignItems="center" spacing={1}>
                        <MUIBox
                            sx={{
                                width: 10,
                                height: 10,
                                backgroundColor: materialUiTheme.palette.primary.main,
                                flexShrink: 0,
                            }}
                        />
                        <MUITypography component="span" variant="subtitle2" sx={{ fontWeight: 400 }}>
                            {Format.number(productionChartTooltipState.tooltipCount)} Worked
                        </MUITypography>
                    </MUIStack>
                </MUIBox>,
                document.body
            )}
        </>
    );
}

/**
 * Dashboard widget showing today's user production and the configured historical production trend.
 *
 * @param {object} props
 * @param {object} props.dashboardWidget Dashboard widget configuration.
 * @param {boolean} props.isActive Whether this dashboard widget is active.
 * @param {number} props.widgetId Dashboard widget identifier used by the data endpoint.
 * @returns {React.ReactElement}
 */
export default function WidgetDashboardUserProduction(props) {
    const userProductionWidgetConfiguration = JSON.parse(props.dashboardWidget.jsonConfiguration);

    return (
        <Widget
            getWidgetId={() => props.widgetId}
            forceRefresh={true}
            getTitle={() => props.dashboardWidget.name}
            isActive={() => props.isActive}
            getSubtitle={(widgetRenderProperties) => {
                const requestedDayCount = userProductionWidgetConfiguration.beginDate.properties.value;
                const dailyAverage = calculateUserProductionDailyAverage(
                    widgetRenderProperties.widgetData,
                    requestedDayCount
                );
                return `${Format.number(requestedDayCount)}D Average: ${Format.number(dailyAverage, {
                    decimalPlaceCount: 1,
                    shouldRoundValueToDecimalPlacesBeforeFormatting: true,
                })}`;
            }}
            renderContent={(widgetRenderProperties) => {
                const todayProductionCount = sumTodayUserProductionCount(widgetRenderProperties.widgetData);
                return (
                    <MUITypography
                        component="div"
                        variant="h5"
                        color={todayProductionCount === 0 ? "text.secondary" : "text.primary"}
                    >
                        {Format.number(todayProductionCount)}
                    </MUITypography>
                );
            }}
            renderVisual={(widgetRenderProperties) => (
                <WidgetDashboardUserProductionChart
                    chartEntries={buildUserProductionChartEntries(
                        widgetRenderProperties.widgetData,
                        userProductionWidgetConfiguration.display
                    )}
                    metric={userProductionWidgetConfiguration.metric}
                />
            )}
        />
    );
}
